import type { ManagerialDashboardCampaignPerformance } from '@/types/api'
import {
  formatFullCurrencyBrl,
  formatPercentDisplay,
  resolveMarginPercent,
} from './managerial-kpis'

export type CampaignPerformanceStatus = ManagerialDashboardCampaignPerformance['status']

export type CampaignPerformance = {
  id: string
  campaignName: string
  clientName: string
  revenue: number
  costs: number
  profit: number
  status: CampaignPerformanceStatus
}

export type CampaignPerformanceView = {
  id: string
  campaignName: string
  clientName: string
  formattedRevenue: string
  formattedCost: string
  formattedProfit: string
  formattedMargin: string
  status: CampaignPerformanceStatus
  statusLabel: string
}

export const CAMPAIGN_PERFORMANCE_STATUS_LABELS: Record<
  CampaignPerformanceStatus,
  string
> = {
  draft: 'Rascunho',
  active: 'Ativa',
  finished: 'Finalizada',
  cancelled: 'Cancelada',
}

export const EMPTY_CAMPAIGN_PERFORMANCE_VIEWS: CampaignPerformanceView[] = []

export function toCampaignPerformanceView(
  row: CampaignPerformance
): CampaignPerformanceView {
  const margin = resolveMarginPercent(row.revenue, row.profit)

  return {
    id: row.id,
    campaignName: row.campaignName,
    clientName: row.clientName,
    formattedRevenue: formatFullCurrencyBrl(row.revenue),
    formattedCost: formatFullCurrencyBrl(row.costs),
    formattedProfit: formatFullCurrencyBrl(row.profit),
    formattedMargin: formatPercentDisplay(margin),
    status: row.status,
    statusLabel: CAMPAIGN_PERFORMANCE_STATUS_LABELS[row.status],
  }
}

export function mapCampaignPerformance(
  rows: ManagerialDashboardCampaignPerformance[]
): CampaignPerformanceView[] {
  return rows.map((row) =>
    toCampaignPerformanceView({
      id: String(row.id),
      campaignName: row.campaign_name,
      clientName: row.client_name,
      revenue: row.revenue,
      costs: row.costs,
      profit: row.profit,
      status: row.status,
    })
  )
}

/** Fixture de formatação da tabela (não vai para a página). */
export const PLACEHOLDER_CAMPAIGN_PERFORMANCE: CampaignPerformance[] = [
  {
    id: 'missao-onca-pintada',
    campaignName: 'Missão Onça Pintada',
    clientName: 'Instituto Onça Pintada',
    revenue: 642_880,
    costs: 412_300,
    profit: 230_580,
    status: 'active',
  },
  {
    id: 'super-premios',
    campaignName: 'Super Prêmios',
    clientName: 'Leo Rações',
    revenue: 298_320,
    costs: 206_180,
    profit: 92_140,
    status: 'active',
  },
  {
    id: 'verao-premiado',
    campaignName: 'Verão Premiado',
    clientName: 'SantaPlay',
    revenue: 271_450,
    costs: 198_600,
    profit: 72_850,
    status: 'active',
  },
  {
    id: 'raspadinha-premiada',
    campaignName: 'Raspadinha Premiada',
    clientName: 'Nissin',
    revenue: 198_760,
    costs: 142_300,
    profit: 56_460,
    status: 'active',
  },
  {
    id: 'natal-premiado',
    campaignName: 'Natal Premiado',
    clientName: 'Supermercados Ipanema',
    revenue: 176_320,
    costs: 131_090,
    profit: 45_230,
    status: 'draft',
  },
]

export function getPlaceholderCampaignPerformanceViews(): CampaignPerformanceView[] {
  return PLACEHOLDER_CAMPAIGN_PERFORMANCE.map(toCampaignPerformanceView)
}
